import { useTranslation } from "react-i18next";
import { ArrowRotateLeft, SearchNormal1 } from "iconsax-react";
import cn from "classnames";
import { Input, Checkbox, DatePicker } from "@/shared/ui";
import type { RangeValue } from "@/shared/ui";
import type { Locale } from "@/shared/utils";
import type { PendingCategory } from "../consts";
import { PENDING_CATEGORIES } from "../consts";
import type { Priority } from "../types/pending.types";

const PRIORITIES: Priority[] = ["urgent", "high", "normal", "low"];

interface Props {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedCategories: Set<PendingCategory>;
  onCategoryToggle: (category: PendingCategory) => void;
  selectedPriorities: Set<Priority>;
  onPriorityToggle: (priority: Priority) => void;
  dateRange: RangeValue | null;
  onDateRangeChange: (value: RangeValue | null) => void;
  onReset: () => void;
  hasActiveFilters: boolean;
}

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-label-sm content-base-primary">{title}</span>
      {children}
    </div>
  );
}

export default function PendingActionsFilterPanel({
  searchQuery,
  onSearchChange,
  selectedCategories,
  onCategoryToggle,
  selectedPriorities,
  onPriorityToggle,
  dateRange,
  onDateRangeChange,
  onReset,
  hasActiveFilters,
}: Props) {
  const { t, i18n } = useTranslation("PendingActionsPage");
  const locale = (i18n.language as Locale) || "ru";

  return (
    <aside className="w-2xs shrink-0 flex flex-col radius-lg surface-base-fill overflow-hidden self-start">
      <div className="border-b surface-container-stroke px-5 py-4 flex items-center justify-between">
        <p className="text-label-lg content-base-primary">{t("filterPanel.filtersTitle")}</p>
        {hasActiveFilters && (
          <button
            type="button"
            className="flex justify-end items-center gap-1 text-label-sm content-base-secondary cursor-pointer"
            onClick={onReset}>
            {t("filterPanel.resetAll")}
            <ArrowRotateLeft size={16} color="currentColor" />
          </button>
        )}
      </div>

      <div className="px-5 py-4">
        <Input
          placeholder={t("filterPanel.searchPlaceholder")}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          icon={<SearchNormal1 size={16} color="currentColor" />}
        />
      </div>

      <div className="border-t surface-container-stroke px-5 py-4">
        <FilterSection title={t("filterPanel.category")}>
          <div className="flex flex-wrap gap-2">
            {PENDING_CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                className={cn(
                  "px-3 py-1.5 radius-sm text-label-sm cursor-pointer transition-colors duration-150",
                  selectedCategories.has(cat)
                    ? "background-brand-fill content-static-white"
                    : "surface-component-fill content-base-primary hover:opacity-80",
                )}
                onClick={() => onCategoryToggle(cat)}>
                {t(`categories.${cat}`)}
              </button>
            ))}
          </div>
        </FilterSection>
      </div>

      <div className="border-t surface-container-stroke px-5 py-4">
        <FilterSection title={t("filterPanel.priority")}>
          <div className="flex flex-col gap-1">
            {PRIORITIES.map((priority) => (
              <Checkbox
                key={priority}
                state={selectedPriorities.has(priority) ? "checked" : "unchecked"}
                onChange={() => onPriorityToggle(priority)}
                label={t(`priority.${priority}`)}
              />
            ))}
          </div>
        </FilterSection>
      </div>

      <div className="border-t surface-container-stroke px-5 py-4">
        <FilterSection title={t("filterPanel.dateRange")}>
          <DatePicker
            mode="range"
            value={dateRange}
            onChange={onDateRangeChange}
            placeholder={t("filterPanel.dateRange")}
            locale={locale}
          />
        </FilterSection>
      </div>
    </aside>
  );
}
